/**
 * Performance History hook (CR-4751).
 *
 * Fetches the data needed by the Cached Performance tab:
 *   - Latest snapshot per metric type (for KPI cards)
 *   - Trend series for every metric that drives a chart
 *   - Target thresholds from CustomerPerformanceSettings for target lines
 *
 * Accepts a period type + optional date range so the caller (tab) can surface
 * a "Last 3/6/12 months / Custom" selector without the hook knowing anything
 * about UI widgets.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  dashboardSnapshotsAPI,
  type DashboardSnapshot,
  type MetricType,
  type PeriodType,
  type TrendPoint,
} from '../lib/api/dashboard-snapshots';
import { performanceSettingsAPI } from '../lib/api/performanceSettings';
import { getErrorMessage } from './useFetchData';

export interface PerformanceTargets {
  ppm: number | null;
  otif: number | null;
  otd: number | null;
}

export interface PerformanceHistoryData {
  latest: DashboardSnapshot[];
  /**
   * State metrics (open_order_value, arrears_*, ncr_open_count,
   * active_customer_count) pulled from the DAILY period and surfaced here so
   * the UI can fall back when the selected period has no state-metric
   * coverage (e.g. Weekly/Quarterly have none — they aren't backfilled).
   */
  stateLatest: DashboardSnapshot[];
  trends: Record<MetricType, TrendPoint[]>;
  targets: PerformanceTargets;
}

export interface PerformanceHistoryOptions {
  periodType?: PeriodType;
  dateFrom?: string;
  dateTo?: string;
}

export interface PerformanceHistoryResult {
  data: PerformanceHistoryData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CHART_METRICS: MetricType[] = [
  'open_order_value',
  'total_spend',
  'monthly_spend',
  'arrears_value',
  'arrears_count',
  'arrears_opening_value',
  'arrears_new_value',
  'arrears_resolved_value',
  'arrears_closing_value',
  'otif_percentage',
  'otd_percentage',
  'on_time_count',
  'early_count',
  'late_count',
  'total_grns',
  'ppm',
  'ncr_count',
  'ncr_open_count',
  'active_customer_count',
  'appeals_count',
];

/**
 * Fetch the full data bundle for the Cached Performance tab.
 *
 * @param options - Period type + optional date range
 * @returns PerformanceHistoryResult with latest + trends + targets
 */
export function usePerformanceHistory(
  options: PeriodType | PerformanceHistoryOptions = 'monthly',
): PerformanceHistoryResult {
  const normalized: PerformanceHistoryOptions =
    typeof options === 'string' ? { periodType: options } : options;
  const periodType: PeriodType = normalized.periodType ?? 'monthly';
  const { dateFrom, dateTo } = normalized;

  const [data, setData] = useState<PerformanceHistoryData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch DAILY latest too — state metrics are only forward-captured
      // on the daily cadence (backfill skips them for historical accuracy).
      // Surfaced as a fallback so Weekly/Quarterly views can still show the
      // current Open Orders / Arrears / Open NCRs values.
      const [latestRes, stateLatestRes, settingsRes] = await Promise.all([
        dashboardSnapshotsAPI.latest(periodType),
        periodType === 'daily'
          ? Promise.resolve({ data: [] as DashboardSnapshot[] })
          : dashboardSnapshotsAPI.latest('daily').catch(() => ({ data: [] as DashboardSnapshot[] })),
        performanceSettingsAPI.getSettings().catch(() => null),
      ]);
      const trendResponses = await Promise.all(
        CHART_METRICS.map((metric) =>
          dashboardSnapshotsAPI
            .trends({
              metric_type: metric,
              period_type: periodType,
              ...(dateFrom ? { date_from: dateFrom } : {}),
              ...(dateTo ? { date_to: dateTo } : {}),
            })
            .then((r) => [metric, r.data] as const)
            .catch(() => [metric, [] as TrendPoint[]] as const),
        ),
      );
      const trends = Object.fromEntries(trendResponses) as Record<MetricType, TrendPoint[]>;

      const settings = settingsRes?.data;
      const targets: PerformanceTargets = {
        ppm: settings ? Number(settings.ppm_good_threshold) : null,
        otif: settings ? Number(settings.otif_good_threshold) : null,
        // CR-4542: otd shares otif_good_threshold as target
        otd: settings ? Number(settings.otif_good_threshold) : null,
      };

      setData({
        latest: latestRes.data,
        stateLatest: stateLatestRes.data,
        trends,
        targets,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load performance history'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodType, dateFrom, dateTo]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, refetch: fetchAll };
}
